/**
 * SystemMetrics - Solar system metrics display
 *
 * Shows module count, areas, coverage percentage, and total capacity.
 */

import React from 'react';

interface SystemMetricsProps {
  moduleCount: number;
  moduleWidth: number;
  moduleHeight: number;
  modulePowerWatts: number;
  roofArea?: number;
}

export function SystemMetrics({
  moduleCount,
  moduleWidth,
  moduleHeight,
  modulePowerWatts,
  roofArea,
}: SystemMetricsProps) {
  if (moduleCount === 0) return null;

  const moduleArea = moduleWidth * moduleHeight;
  const totalArea = moduleCount * moduleArea;
  const capacityKw = (moduleCount * modulePowerWatts / 1000).toFixed(2);
  const coveragePercent = roofArea ? ((totalArea / roofArea) * 100).toFixed(1) : null;

  return (
    <div className="pt-2 border-t border-gray-300 mt-2">
      <h5 className="font-semibold text-gray-700 mb-2">System Metrics</h5>
      <div className="space-y-1 text-xs bg-blue-50 p-2 rounded">
        <div className="flex justify-between">
          <span>Module Count:</span>
          <span className="font-semibold">{moduleCount}</span>
        </div>
        <div className="flex justify-between">
          <span>Module Area:</span>
          <span className="font-semibold">{moduleArea.toFixed(2)} m²</span>
        </div>
        <div className="flex justify-between">
          <span>Total Area:</span>
          <span className="font-semibold">{totalArea.toFixed(2)} m²</span>
        </div>
        {coveragePercent !== null && (
          <div className="flex justify-between">
            <span>Roof Coverage:</span>
            <span className="font-semibold">{coveragePercent}%</span>
          </div>
        )}
        <div className="flex justify-between border-t border-blue-200 pt-1 mt-1">
          <span>System Capacity:</span>
          <span className="font-bold text-blue-700">{capacityKw} kW</span>
        </div>
      </div>
    </div>
  );
}

export default SystemMetrics;
